import { isEmpty, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { SimNaoRadioListField } from 'components/form/field/SimNaoRadioListField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import React from 'react'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { ConsultaSeguimentoPerguntaHeaderTitle } from '../../components/ConsultaSeguimentoPerguntaHeaderTitle'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import { hasAnySim } from '../../utils-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta03ExemplosEnum,
  ConsultaSeguimentoPergunta03Model,
} from './model-consultaSeguimentoPergunta03'
import { isPergunta03ExemplosAllNao } from './utils-consultaSeguimentoPergunta03'

interface ConsultaSeguimentoPergunta03FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta03Model>
}

export function ConsultaSeguimentoPergunta03Field(props: Readonly<ConsultaSeguimentoPergunta03FieldProps>) {
  const { name } = props

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta03Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta03Model>({
    name,
    conditionToCalculate,
    calculateResultado,
  })

  const isPerguntaHeaderSim = value.PERGUNTA_03_HEADER === SimNaoEnum.SIM

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_03}
          title={<ConsultaSeguimentoPerguntaHeaderTitle text='brinca de faz de contas?' />}
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_03_HEADER} />}
        />
        {!isEmpty(value.PERGUNTA_03_HEADER) && (
          <VFlow>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              {isPerguntaHeaderSim && (
                <ConsultaSeguimentoIndicatorHelper
                  title='“Por favor, me dê um exemplo de uma das brincadeiras de faz de contas do seu filho.”'
                  subtitle='(Se o genitor não der um dos exemplos abaixo, perguntar cada um deles individualmente.)'
                />
              )}
            </VFlow>
            <SimNaoRadioListField<ConsultaSeguimentoPergunta03ExemplosEnum>
              name={name.PERGUNTA_03_EXEMPLOS}
              title='O seu filho algum vez...'
              description={
                isPerguntaHeaderSim
                  ? 'Responda os exemplos até pontuar "Passou" ou “Falhou”'
                  : 'Pergunte cada um dos exemplos até pontuar "Passou" ou “Falhou”'
              }
              options={PERGUNTA_03_EXEMPLOS_OPTIONS}
              required
            />
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{ FALHOU: 'Não para todos:', PASSOU: 'Sim para qualquer um' }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_03_EXEMPLOS_OPTIONS: RadioOption<ConsultaSeguimentoPergunta03ExemplosEnum>[] = [
  {
    label: 'Fez de conta que bebia em uma xícara de brinquedo?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.BEBIA_XICARA_BRINQUEDO,
  },
  {
    label: 'Fez de conta que comia com uma colher ou um garfo de brinquedo?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.COMIA_COLHER_BRINQUEDO,
  },
  {
    label: 'Fez de conta que falava no telefone?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.FALAVA_TELEFONE,
  },
  {
    label:
      'Fez de conta que dava comida a uma boneca ou a um bichinho de pelúcia, com comida de verdade ou de brinquedo?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.DAVA_COMIDA,
  },
  {
    label: 'Brincou de empurrar um carro de brinquedo, como se o carro andasse em uma estrada?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.EMPURRAR_CARRO,
  },
  {
    label: 'Fez de conta que era um robô, um avião, uma bailarina ou algum dos seus personagens favoritos?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.PERSONAGENS_FAVORITOS,
  },
  {
    label: 'Colocou uma panela de brinquedo em um fogão de brinquedo?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.PANELA_FOGAO,
  },
  {
    label: 'Mexeu uma comida de mentirinha?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.MEXEU_COMIDA,
  },
  {
    label: 'Colocou um boneco dentro de um carro ou um caminhão como se o boneco fosse o motorista ou passageiro?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.MOTORISTA_PASSAGEIRO,
  },
  {
    label: 'Fez de conta que aspirava o tapete, varria o chão ou cortava a grama?',
    value: ConsultaSeguimentoPergunta03ExemplosEnum.ASPIRAVA_VARRIA_CORTAVA,
  },
]

const conditionToCalculate = (values: ConsultaSeguimentoPergunta03Model): boolean => {
  return (
    !isEmpty(values.PERGUNTA_03_HEADER) &&
    (hasAnySim(values.PERGUNTA_03_EXEMPLOS) || isPergunta03ExemplosAllNao(values.PERGUNTA_03_EXEMPLOS))
  )
}

const calculateResultado = (values: ConsultaSeguimentoPergunta03Model): PassouFalhouEnum => {
  if (!isEmpty(values.PERGUNTA_03_HEADER) && hasAnySim(values.PERGUNTA_03_EXEMPLOS)) {
    return PassouFalhouEnum.PASSOU
  }
  if (!isEmpty(values.PERGUNTA_03_HEADER) && isPergunta03ExemplosAllNao(values.PERGUNTA_03_EXEMPLOS)) {
    return PassouFalhouEnum.FALHOU
  }
}
